/**
 * Customer address tags — shared client/server.
 *
 * A customer's address book carries two independent axes:
 *
 *   • `isPrimary` — exactly one **Main** address. It's the default site for
 *     new jobs and the one mirrored onto the legacy single-address fields.
 *     One per customer, enforced by customer.service.ts.
 *   • `tags[]` — what KIND of place it is: the holiday house, an investment
 *     property, the brother's place. Many per address, and anything outside
 *     the preset catalogue below is allowed as a free-text tag.
 *
 * There is deliberately no `main` preset — "main" is `isPrimary`, and having
 * both would let an address be tagged main while a different one actually is.
 *
 * `tone` is an abstract colour token, not a class name: shared/ stays free of
 * presentation, the client maps tone → Tailwind (see AddressTagChips.tsx) and
 * the server ignores it entirely.
 *
 * Slugs are what gets stored. Renaming a `label` is safe; renaming a KEY needs
 * a data migration.
 */

export type AddressTagTone =
  | 'blue'
  | 'cyan'
  | 'emerald'
  | 'amber'
  | 'violet'
  | 'rose'
  | 'slate';

export interface AddressTagPreset {
  slug: string;
  label: string;
  tone: AddressTagTone;
}

/** The preset chip row in the address-book editor. Order is display order. */
export const ADDRESS_TAG_PRESETS: readonly AddressTagPreset[] = [
  { slug: 'home', label: 'Home', tone: 'blue' },
  { slug: 'holiday', label: 'Holiday', tone: 'cyan' },
  { slug: 'investment', label: 'Investment', tone: 'emerald' },
  { slug: 'rental', label: 'Rental', tone: 'emerald' },
  { slug: 'business', label: 'Business', tone: 'violet' },
  { slug: 'family', label: 'Family', tone: 'rose' },
  { slug: 'site', label: 'Site', tone: 'amber' },
  { slug: 'billing', label: 'Billing', tone: 'slate' },
  { slug: 'postal', label: 'Postal', tone: 'slate' },
  { slug: 'storage', label: 'Storage', tone: 'slate' },
] as const;

const PRESET_BY_SLUG: Record<string, AddressTagPreset> = Object.fromEntries(
  ADDRESS_TAG_PRESETS.map((p) => [p.slug, p]),
);

/** Chips per address. Keeps the row readable and the doc small. */
export const MAX_ADDRESS_TAGS = 6;

/** Longest a single custom tag may be, in characters. */
export const MAX_ADDRESS_TAG_LENGTH = 24;

/**
 * Canonical stored form of a tag: lowercase, single-spaced, trimmed.
 *
 * Spaces and apostrophes survive on purpose — a custom tag is a human phrase
 * ("brother's place"), not an identifier, and stripping them would render back
 * as "Brothers Place". Everything outside letters/digits/space/'/-/&/ is
 * dropped so a stray comma can't split one tag into two on display.
 *
 * Returns '' for anything that normalizes away — callers should drop those.
 */
export function normalizeAddressTag(raw: string): string {
  return (raw || '')
    .normalize('NFC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N} '&/-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_ADDRESS_TAG_LENGTH)
    .trim();
}

/**
 * Normalize, drop empties, de-duplicate (first occurrence wins so the user's
 * ordering is preserved) and cap at MAX_ADDRESS_TAGS.
 */
export function dedupeAddressTags(tags: readonly string[] | undefined): string[] {
  if (!tags || tags.length === 0) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of tags) {
    const slug = normalizeAddressTag(raw);
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    out.push(slug);
    if (out.length >= MAX_ADDRESS_TAGS) break;
  }
  return out;
}

/**
 * Display form of a stored slug. Presets use their curated label; a custom tag
 * is title-cased per word, leaving intra-word punctuation alone so
 * "brother's place" reads as "Brother's Place" and not "Brother'S Place".
 */
export function addressTagLabel(slug: string): string {
  const norm = normalizeAddressTag(slug);
  if (!norm) return '';
  const preset = PRESET_BY_SLUG[norm];
  if (preset) return preset.label;
  return norm
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/** Colour token for a stored slug. Custom tags all render neutral. */
export function addressTagTone(slug: string): AddressTagTone {
  return PRESET_BY_SLUG[normalizeAddressTag(slug)]?.tone ?? 'slate';
}

/** True when the slug is one of the curated presets rather than a custom tag. */
export function isPresetAddressTag(slug: string): boolean {
  return !!PRESET_BY_SLUG[normalizeAddressTag(slug)];
}

/**
 * Comma-joined display labels — used by the server-side text serializers
 * (RAG ingest, askTheo lookup_customer) so "where's the holiday house?"
 * has something to match on. Returns '' when there are no usable tags.
 */
export function formatAddressTags(tags: readonly string[] | undefined): string {
  return dedupeAddressTags(tags).map(addressTagLabel).filter(Boolean).join(', ');
}
